// navigation-completion Stage E — hyperlink list + edit controls.
//
// The clickable source area and the destination landing point are chosen VISUALLY
// on the rendered PDF (draw a box or select text) via the workspace viewer, with
// precise numeric PDF coordinates kept as a collapsed fallback. A SAVED remote
// (cross-document) link reads back WHERE it points and offers an authorised
// exact-byte preview of its target, resolved through the inventory — never a raw
// path fetch. Styling is a SEPARATELY gated operation (design §4B) with its own
// policy decision; a denied control shows its reason.

import type { RemoteNavigationPreviewCallback } from './useRemoteNavigationPreview';
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { ApiError, parseApiErrorBody } from '@/api/v2Client';
import { navigationTargets } from '@/api/navigationTargets';
import type {
  DestinationPoint,
  InventoryDestination,
  InventoryLink,
  LinkStyle,
  NavEdit,
  OperationDecisions,
  Rectangle,
} from '@/api/documentNavigation';
import {
  authReasonText,
  describeDestination,
  draftFromTarget,
  navStyles,
  targetFromDraft,
  EMPTY_LOCAL_TARGET,
  EMPTY_REMOTE_TARGET,
  type TargetDraft,
} from './navShared';
import { TargetFields } from './navTargetFields';
import { describeSavedRemoteMiss, matchSavedRemoteTarget } from './remoteSavedTarget';
import type { SavedRemoteSeed } from './RemoteTargetPicker';
import type { PdfPreviewFocus, PdfSelectionResult, SelectionKind } from './PdfCanvasViewer';

let newLinkCounter = 0;
function freshHandle(): string {
  newLinkCounter += 1;
  return `new-link-${newLinkCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

interface RectDraft {
  readonly x0: string;
  readonly y0: string;
  readonly x1: string;
  readonly y1: string;
}
const EMPTY_RECT: RectDraft = { x0: '', y0: '', x1: '', y1: '' };

function rectFromDraft(
  d: RectDraft,
): { readonly ok: true; readonly rect: { x0: number; y0: number; x1: number; y1: number } } | { readonly ok: false; readonly error: string } {
  const nums = [Number(d.x0), Number(d.y0), Number(d.x1), Number(d.y1)];
  if (nums.some((n) => !Number.isFinite(n))) {
    return { ok: false, error: 'all four rectangle coordinates must be finite numbers' };
  }
  const [x0, y0, x1, y1] = nums as [number, number, number, number];
  if (x1 <= x0 || y1 <= y0) {
    return { ok: false, error: 'the rectangle must have x1 > x0 and y1 > y0' };
  }
  return { ok: true, rect: { x0, y0, x1, y1 } };
}

/** A small marker rectangle anchored at a saved /XYZ landing point (its top-left
 *  = the point), so "View destination" highlights the EXACT saved position on the
 *  page rather than just scrolling to the page. The viewer clamps it to the page,
 *  so a point near an edge still paints inside the page. */
function landingMarker(at: DestinationPoint): Rectangle {
  const W = 28;
  const H = 14;
  return { x0: at.x, y0: at.y - H, x1: at.x + W, y1: at.y };
}

/** A saved REMOTE destination expressed as a picker seed, so "Change target"
 *  re-selects the existing cross-document destination. Undefined when the remote
 *  link recorded no addressable page/named destination. */
function savedRemoteFromDestination(
  d: InventoryDestination,
  authorized?: { targetId: string; contextVersion: string; expectedTargetSha256: string },
): SavedRemoteSeed | undefined {
  if (d.kind !== 'remote') return undefined;
  const auth = authorized ? { authorized } : {};
  if (typeof d.page === 'number') {
    return { href: d.path, destination: d.at ? { kind: 'page', pageIndex: d.page, at: d.at } : { kind: 'page', pageIndex: d.page }, ...auth };
  }
  if (d.name) return { href: d.path, destination: { kind: 'named', name: d.name }, ...auth };
  return undefined;
}

/** The preview focus for a saved remote destination, shown against the TARGET's
 *  own bytes: the saved page (+ landing-point highlight) or named destination. */
function remoteDestFocus(d: Extract<InventoryDestination, { kind: 'remote' }>, label: string): PdfPreviewFocus | null {
  if (typeof d.page === 'number') {
    return {
      page: d.page + 1,
      tone: 'destination',
      ...(d.at ? { highlight: landingMarker(d.at) } : {}),
      label: d.at ? `${label} · page ${d.page + 1} at (${d.at.x.toFixed(0)}, ${d.at.y.toFixed(0)})` : `${label} · page ${d.page + 1}`,
    };
  }
  if (d.name) return { named: d.name, tone: 'destination', label: `${label} · “${d.name}”` };
  return null;
}

function errorText(error: unknown): string {
  const body = parseApiErrorBody(error);
  const message = body?.message ?? body?.error ?? (error instanceof Error ? error.message : String(error));
  return error instanceof ApiError && error.status === 409
    ? `${message} — refresh and reselect the destination.`
    : message;
}

/** A short, plain-language summary of a destination draft for the add-form
 *  review line (so the reviewer sees where the link points before creating it). */
function describeTargetDraft(d: TargetDraft): string {
  if (d.kind === 'named') return d.name.trim() ? `named destination “${d.name.trim()}”` : 'a named destination (not set)';
  if (d.kind === 'remote') return d.selection ? 'the selected other document' : 'another document (not selected)';
  const atText = d.at ? ` at (${d.at.x.toFixed(0)}, ${d.at.y.toFixed(0)})` : '';
  return `page ${d.pageInput}${atText}`;
}

/** `#rrggbb` → [r,g,b] in [0,1], or null when not a 6-hex colour. */
function hexToRgb(hex: string): readonly [number, number, number] | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const v = m[1] ?? '';
  const r = parseInt(v.slice(0, 2), 16) / 255;
  const g = parseInt(v.slice(2, 4), 16) / 255;
  const b = parseInt(v.slice(4, 6), 16) / 255;
  return [r, g, b];
}

export interface HyperlinkEditorProps {
  readonly links: readonly InventoryLink[];
  readonly annotationIncomplete: boolean;
  readonly pageCount: number | null;
  readonly canMutate: boolean;
  readonly mutationBlockedReason: string | null;
  readonly decisions: OperationDecisions;
  readonly busy: boolean;
  readonly onEdit: (edit: NavEdit, actionKey: string) => void;
  readonly onJumpToPage: (page1: number) => void;
  /** Rich preview request (page + source/destination rect highlight + tone).
   *  When present the "View source"/"View destination" affordances route through
   *  it; the plain {@link onJumpToPage} remains the fallback for a build without a
   *  canvas viewer. */
  readonly onPreviewFocus?: (focus: PdfPreviewFocus) => void;
  /** Preview a cross-document target's exact bytes in the workspace viewer. */
  readonly onPreview?: RemoteNavigationPreviewCallback;
  /** The source document id — enables the cross-document target picker. */
  readonly documentId: string;
  /** Reports whether a row retarget/style or the add form is open with unsaved
   *  input, so the parent can register it as workspace draft state. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  /**
   * Arm a visual selection on the workspace canvas. `source` draws the clickable
   * link rectangle; `destination` picks the landing area. The viewer resolves the
   * gesture to PDF-space geometry on the exact page and calls `onResult`. Absent
   * in a build without the canvas viewer — the numeric fallback still works.
   */
  readonly onBeginSelect?: (kind: SelectionKind, onResult: (r: PdfSelectionResult) => void, opts?: { keepPreview?: boolean }) => void;
  /** Bumped by the workspace whenever the cross-document preview is cleared /
   *  returned. An in-flight saved-remote "View destination" fetch drops its result
   *  if this changes, so a late completion never revives a left preview. */
  readonly remotePreviewNonce?: number;
}

export function HyperlinkEditor({
  links,
  annotationIncomplete,
  pageCount,
  canMutate,
  mutationBlockedReason,
  decisions,
  busy,
  onEdit,
  onJumpToPage,
  onPreviewFocus,
  onPreview,
  documentId,
  onDirtyChange,
  onBeginSelect,
  remotePreviewNonce,
}: HyperlinkEditorProps): ReactElement {
  const [editing, setEditing] = useState<{ handle: string; mode: 'retarget' | 'style' } | null>(null);
  const [retargetDraft, setRetargetDraft] = useState<TargetDraft>(EMPTY_LOCAL_TARGET);
  const [styleWidth, setStyleWidth] = useState('1');
  const [styleColor, setStyleColor] = useState('#0000ff');
  const [adding, setAdding] = useState(false);
  const [addPage, setAddPage] = useState('1');
  const [addRect, setAddRect] = useState<RectDraft>(EMPTY_RECT);
  const [addDraft, setAddDraft] = useState<TargetDraft>(EMPTY_LOCAL_TARGET);
  // The text the source rectangle was selected FROM (PDF.js text layer), kept for
  // the source → destination review line; null for a drawn box / scanned page.
  const [addSourceText, setAddSourceText] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  // The link handle whose saved-remote-target preview is currently resolving (so
  // its "View destination" shows progress and can't be double-fired).
  const [remoteViewBusy, setRemoteViewBusy] = useState<string | null>(null);
  const previewOwner = useId();
  // Epoch for the saved-remote-target preview fetch: bumped on each new request
  // AND on unmount, so a stale completion (a competing View click, or the link
  // being removed/retargeted / the reviewer returning to source / the editor
  // remounting on a new revision before the fetch resolves) can never revive a
  // superseded preview or setState into a gone editor.
  const viewEpochRef = useRef(0);
  useEffect(() => () => { viewEpochRef.current += 1; }, []);
  // Live mirror of the workspace's preview nonce; a change means the reviewer
  // returned to source / switched preview, invalidating an in-flight View fetch.
  const previewNonceRef = useRef(remotePreviewNonce);
  previewNonceRef.current = remotePreviewNonce;
  useEffect(() => { onDirtyChange?.(editing !== null || adding); }, [editing, adding, onDirtyChange]);

  // Preview a SAVED remote link's destination against the authorised target's
  // EXACT bytes: resolve its stored href to a unique inventory target (hash +
  // context re-verified by the preview endpoint), fetch those bytes, and show the
  // saved page/named destination + landing point. Never a raw-path fetch; a
  // stale/ambiguous/unavailable target is refused with an honest message.
  async function viewRemoteDestination(
    handle: string,
    dest: Extract<InventoryDestination, { kind: 'remote' }>,
    descriptor?: { targetId: string; contextVersion: string; expectedTargetSha256: string },
  ): Promise<void> {
    if (!onPreview) return;
    const epoch = ++viewEpochRef.current;
    const startNonce = previewNonceRef.current;
    // Apply the RESULT only while NOT superseded by a newer request/unmount AND the
    // reviewer has not returned to source / switched the preview (nonce unchanged).
    const live = (): boolean => epoch === viewEpochRef.current && previewNonceRef.current === startNonce;
    // Ownership of the BUSY flag is EPOCH only: this request must clear its own
    // "Loading…" even when the preview intent nonce changed (a return-to-source
    // superseded it), yet must never clear a NEWER request's busy state (Kimi F2).
    const ownsBusy = (): boolean => epoch === viewEpochRef.current;
    setRemoteViewBusy(handle);
    setLocalError(null);
    try {
      let targetId: string;
      let contextVersion: string;
      let expectedTargetSha256: string;
      let label: string;
      if (descriptor) {
        // AUTHORED + current: use the server-CHECKED tokens verbatim (from the same
        // inventory the currency check ran against). The preview endpoint re-checks
        // context + hash and refuses a changed one, so there is NO second client
        // inventory fetch and no stale read-window.
        ({ targetId, contextVersion, expectedTargetSha256 } = descriptor);
        label = dest.path.split('/').pop() || dest.path;
      } else {
        // IMPORTED/unbound: current-resolution against a fresh inventory.
        const inventory = await navigationTargets.getInventory(documentId);
        if (!live()) return;
        const match = matchSavedRemoteTarget(inventory, dest.path);
        if (match.kind !== 'ok') {
          setLocalError(`Cannot preview the saved target: ${describeSavedRemoteMiss(match)}.`);
          return;
        }
        targetId = match.target.id;
        contextVersion = inventory.contextVersion;
        expectedTargetSha256 = match.target.artifactSha256;
        label = match.target.label;
      }
      const response = await navigationTargets.preview(documentId, targetId, { contextVersion, expectedTargetSha256 });
      const blob = await response.blob();
      // Superseded while fetching (competing request / unmount / document change /
      // return-to-source)? Drop it rather than revive a stale preview.
      if (!live()) return;
      onPreview(blob, label, remoteDestFocus(dest, label), previewOwner);
    } catch (error) {
      // A changed context/hash comes back as a 409 here (the server caught the
      // race) — surface it honestly, but only while this request is still live.
      if (live()) setLocalError(errorText(error));
    } finally {
      if (ownsBusy()) setRemoteViewBusy(null);
    }
  }

  const createDec = decisions.createHyperlink;
  const modifyDec = decisions.modifyHyperlink;
  const styleDec = decisions.style;
  const canCreate = canMutate && !!createDec?.allowed;
  const canModify = canMutate && !!modifyDec?.allowed;
  const canStyle = canMutate && !!styleDec?.allowed;
  const reasonOf = (allowed: boolean, dec: { allowed: boolean; reason: Parameters<typeof authReasonText>[0] } | undefined): string | null =>
    !canMutate ? mutationBlockedReason : !allowed && dec ? authReasonText(dec.reason) : null;
  const createReason = reasonOf(canCreate, createDec);
  const modifyReason = reasonOf(canModify, modifyDec);
  const styleReason = reasonOf(canStyle, styleDec);

  function submitRetarget(handle: string): void {
    const r = targetFromDraft(retargetDraft, pageCount);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setLocalError(null);
    setEditing(null);
    onEdit({ op: 'retargetHyperlink', handle, target: r.target }, `link-retarget-${handle}`);
  }

  function submitStyle(handle: string): void {
    const width = Number(styleWidth);
    if (!Number.isFinite(width) || width < 0) {
      setLocalError('the border width must be a non-negative number');
      return;
    }
    const style: { color?: readonly [number, number, number]; borderWidth?: number } = { borderWidth: width };
    if (styleColor.trim().length > 0) {
      const rgb = hexToRgb(styleColor);
      if (!rgb) {
        setLocalError('the colour must be a 6-digit hex value like #0000ff');
        return;
      }
      style.color = rgb;
    }
    setLocalError(null);
    setEditing(null);
    onEdit({ op: 'styleHyperlink', handle, style: style as LinkStyle }, `link-style-${handle}`);
  }

  function submitAdd(): void {
    const page = Number(addPage);
    if (!Number.isInteger(page) || page < 1) {
      setLocalError('the page must be a whole number of 1 or greater');
      return;
    }
    if (pageCount !== null && page > pageCount) {
      setLocalError(`the document has ${pageCount} page${pageCount === 1 ? '' : 's'}`);
      return;
    }
    const rect = rectFromDraft(addRect);
    if (!rect.ok) {
      setLocalError(rect.error);
      return;
    }
    const target = targetFromDraft(addDraft, pageCount);
    if (!target.ok) {
      setLocalError(target.error);
      return;
    }
    setLocalError(null);
    setAdding(false);
    setAddPage('1');
    setAddRect(EMPTY_RECT);
    setAddDraft(EMPTY_LOCAL_TARGET);
    setAddSourceText(null);
    onEdit({ op: 'createHyperlink', handle: freshHandle(), page, rect: rect.rect, target: target.target }, 'link-create');
  }

  return (
    <section style={navStyles.editorPanel} data-testid="nav-hyperlinks" aria-label="Hyperlinks">
      {annotationIncomplete && (
        <div style={navStyles.note} data-testid="nav-links-incomplete">
          The annotation scan did not finish — the list below may be incomplete, and structural
          edits to it are unsafe.
        </div>
      )}
      {links.length === 0 ? (
        <div style={navStyles.muted} data-testid="nav-links-empty">
          No hyperlink annotations found in this artifact.
        </div>
      ) : (
        <div data-testid="nav-link-list">
          {links.map((lk) => {
            const rowEditing = editing?.handle === lk.handle;
            // The resolved destination as a preview focus (destination tone), or
            // null when it is remote/external/other/none (no in-document page to
            // show — the reviewer uses the target picker's exact-bytes preview).
            const destFocus: PdfPreviewFocus | null =
              lk.destination.kind === 'localPage'
                ? {
                    page: lk.destination.pageIndex + 1,
                    tone: 'destination',
                    // When the destination recorded an /XYZ landing point, highlight
                    // that exact SAVED position on the page — not just "the page"
                    // (Codex finding 3). A small marker anchored at the landing
                    // (left, top); the viewer clamps it to the page.
                    ...(lk.destination.at ? { highlight: landingMarker(lk.destination.at) } : {}),
                    label: lk.destination.at
                      ? `Destination · page ${lk.destination.pageIndex + 1} at (${lk.destination.at.x.toFixed(0)}, ${lk.destination.at.y.toFixed(0)})`
                      : `Destination · page ${lk.destination.pageIndex + 1}`,
                  }
                : lk.destination.kind === 'named'
                  ? { named: lk.destination.name, tone: 'destination', label: `Destination · “${lk.destination.name}”` }
                  : null;
            // A saved REMOTE destination: it has no in-source page to focus, so it
            // gets an authorised exact-byte preview of its TARGET instead, and its
            // saved destination is re-selectable when retargeting.
            const remoteDest = lk.destination.kind === 'remote' ? lk.destination : null;
            // An AUTHORED remote link whose frozen saved target is stale/unreadable
            // (server canonical verdict) is blocked from preview + re-seed; an
            // imported/unbound link (no savedTarget) resolves currently.
            const savedTargetBlocked = !!lk.savedTarget && !lk.savedTarget.current;
            const savedTargetDetail = (lk.savedTarget && !lk.savedTarget.current && lk.savedTarget.detail) || 'the target changed since this link was saved';
            // An authored CURRENT link seeds the picker with the server-checked
            // authorized tokens (the picker re-verifies context/hash); an imported
            // link seeds by href (current-resolution). A blocked link seeds nothing.
            const savedRemote = remoteDest && !savedTargetBlocked
              ? savedRemoteFromDestination(remoteDest, lk.savedTarget?.current ? { targetId: lk.savedTarget.targetId, contextVersion: lk.savedTarget.contextVersion, expectedTargetSha256: lk.savedTarget.expectedTargetSha256 } : undefined)
              : undefined;
            const canViewRemoteDest = remoteDest !== null && !!onPreview && (typeof remoteDest.page === 'number' || !!remoteDest.name);
            return (
              <div key={lk.handle} style={navStyles.row} data-testid={`nav-link-${lk.handle}`}>
                {/* Plain-language source → destination: "From page N → To <target>".
                    The reviewer reads where the link sits and where it points without
                    parsing coordinates. The raw rectangle is technical detail, so it
                    moves to the disclosure below (still available on demand). */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={navStyles.label}>From</span>
                  <span style={navStyles.value} data-testid={`nav-link-from-${lk.handle}`}>page {lk.page}</span>
                  <span aria-hidden style={navStyles.muted}>→</span>
                  <span style={navStyles.label}>To</span>
                  <span style={navStyles.value} data-testid={`nav-link-to-${lk.handle}`}>
                    {describeDestination(lk.destination)}
                  </span>
                </div>
                <details style={{ marginTop: 2 }} data-testid={`nav-link-geometry-${lk.handle}`}>
                  <summary style={{ ...navStyles.muted, cursor: 'pointer' }}>Technical coordinates</summary>
                  <div style={{ ...navStyles.mono, marginTop: 2 }} data-testid={`nav-link-rect-${lk.handle}`}>
                    source {lk.rect
                      ? `[${lk.rect.x0.toFixed(1)}, ${lk.rect.y0.toFixed(1)}, ${lk.rect.x1.toFixed(1)}, ${lk.rect.y1.toFixed(1)}]`
                      : 'rectangle not recorded'}
                  </div>
                  {/* The full relative path + landing coordinates of a saved remote
                      destination live here (technical), not on the plain "To" line. */}
                  {remoteDest && (
                    <div style={{ ...navStyles.mono, marginTop: 2 }} data-testid={`nav-link-remote-detail-${lk.handle}`}>
                      target {remoteDest.path}
                      {remoteDest.at ? ` · lands at (${remoteDest.at.x.toFixed(1)}, ${remoteDest.at.y.toFixed(1)})` : ''}
                    </div>
                  )}
                </details>
                <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    style={navStyles.secondaryBtn}
                    data-testid={`nav-link-jump-${lk.handle}`}
                    onClick={() =>
                      onPreviewFocus
                        ? onPreviewFocus({ page: lk.page, highlight: lk.rect, tone: 'source', label: `Hyperlink source on page ${lk.page}` })
                        : onJumpToPage(lk.page)
                    }
                  >
                    {onPreviewFocus ? 'View source' : 'Jump to source page'}
                  </button>
                  {destFocus && onPreviewFocus && (
                    <button type="button" style={navStyles.secondaryBtn} data-testid={`nav-link-view-dest-${lk.handle}`} onClick={() => onPreviewFocus(destFocus)}>
                      View destination
                    </button>
                  )}
                  {remoteDest && canViewRemoteDest && (
                    <button
                      type="button"
                      style={remoteViewBusy === lk.handle ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
                      disabled={remoteViewBusy === lk.handle}
                      data-testid={`nav-link-view-dest-${lk.handle}`}
                      onClick={() => {
                        if (savedTargetBlocked) { setLocalError(`Cannot preview the saved target: ${savedTargetDetail}.`); return; }
                        // AUTHORED + current: preview with the server-checked tokens
                        // (atomic). Imported link (no verdict): current-resolution.
                        const descriptor = lk.savedTarget?.current
                          ? { targetId: lk.savedTarget.targetId, contextVersion: lk.savedTarget.contextVersion, expectedTargetSha256: lk.savedTarget.expectedTargetSha256 }
                          : undefined;
                        void viewRemoteDestination(lk.handle, remoteDest, descriptor);
                      }}
                    >
                      {remoteViewBusy === lk.handle ? 'Loading target…' : 'View destination'}
                    </button>
                  )}
                  {onBeginSelect && (
                    <RowBtn
                      label="Edit source area"
                      disabled={!canModify || busy}
                      reason={modifyReason}
                      testid={`nav-link-edit-source-${lk.handle}`}
                      onClick={() =>
                        onBeginSelect('source', (r) => {
                          // The source area stays on the link's own page — a page
                          // change is a remove + recreate, not a move.
                          if (r.page !== lk.page) {
                            setLocalError(`the source area must stay on page ${lk.page}; it was drawn on page ${r.page}`);
                            return;
                          }
                          setLocalError(null);
                          onEdit({ op: 'moveHyperlink', handle: lk.handle, rect: r.rect }, `link-move-${lk.handle}`);
                        })
                      }
                    />
                  )}
                  <RowBtn
                    label="Change target"
                    disabled={!canModify || busy}
                    reason={modifyReason}
                    testid={`nav-link-retarget-btn-${lk.handle}`}
                    onClick={() => {
                      setRetargetDraft(
                        lk.destination.kind === 'localPage'
                          ? draftFromTarget({ kind: 'localPage', pageIndex: lk.destination.pageIndex, ...(lk.destination.at ? { at: lk.destination.at } : {}) })
                          : lk.destination.kind === 'named'
                            ? draftFromTarget({ kind: 'named', name: lk.destination.name })
                            : lk.destination.kind === 'remote'
                              ? EMPTY_REMOTE_TARGET
                              : EMPTY_LOCAL_TARGET,
                      );
                      setEditing({ handle: lk.handle, mode: 'retarget' });
                      // A stale/unreadable authored saved target is not pre-selected
                      // (savedRemote is withheld above); say why so the reviewer
                      // knowingly re-chooses the destination.
                      setLocalError(savedTargetBlocked ? `The saved target cannot be reused: ${savedTargetDetail}. Choose the destination again.` : null);
                    }}
                  />
                  <RowBtn
                    label="Style"
                    disabled={!canStyle || busy}
                    reason={styleReason}
                    testid={`nav-link-style-btn-${lk.handle}`}
                    onClick={() => {
                      setStyleWidth('1');
                      setStyleColor('#0000ff');
                      setEditing({ handle: lk.handle, mode: 'style' });
                      setLocalError(null);
                    }}
                  />
                  <RowBtn
                    label="Remove"
                    tone="danger"
                    disabled={!canModify || busy}
                    reason={modifyReason}
                    testid={`nav-link-remove-${lk.handle}`}
                    onClick={() => onEdit({ op: 'removeHyperlink', handle: lk.handle }, `link-remove-${lk.handle}`)}
                  />
                </div>
                {rowEditing && editing?.mode === 'retarget' && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TargetFields value={retargetDraft} onChange={setRetargetDraft} pageCount={pageCount} idPrefix={`nav-link-retarget-${lk.handle}`} documentId={documentId} disabled={!canModify || busy} {...(onPreview ? { onPreview } : {})} {...(onPreviewFocus ? { onPreviewFocus } : {})} {...(onBeginSelect ? { onBeginSelect } : {})} {...(savedRemote ? { savedRemote } : {})} />
                    <button type="button" style={busy || !canModify || (retargetDraft.kind === 'remote' && !retargetDraft.selection) ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn} data-testid={`nav-link-retarget-save-${lk.handle}`} disabled={busy || !canModify || (retargetDraft.kind === 'remote' && !retargetDraft.selection)} onClick={() => submitRetarget(lk.handle)}>
                      Save
                    </button>
                    <button type="button" style={navStyles.secondaryBtn} onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                )}
                {rowEditing && editing?.mode === 'style' && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label style={navStyles.muted}>
                      Border width{' '}
                      <input type="number" min={0} step={0.5} style={{ ...navStyles.input, width: 64 }} data-testid={`nav-link-style-width-${lk.handle}`} aria-label="Border width" value={styleWidth} onChange={(e) => setStyleWidth(e.target.value)} />
                    </label>
                    <label style={navStyles.muted}>
                      Colour{' '}
                      <input type="text" style={{ ...navStyles.input, width: 90 }} data-testid={`nav-link-style-color-${lk.handle}`} aria-label="Colour (hex)" value={styleColor} onChange={(e) => setStyleColor(e.target.value)} />
                    </label>
                    <button type="button" style={busy ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn} data-testid={`nav-link-style-save-${lk.handle}`} disabled={busy} onClick={() => submitStyle(lk.handle)}>
                      Apply style
                    </button>
                    <button type="button" style={navStyles.secondaryBtn} onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        {adding ? (
          <div data-testid="nav-link-add-form" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* -------- 1. source: draw/select it on the page, or type coords -------- */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={navStyles.label}>Source</span>
              {onBeginSelect ? (
                <button
                  type="button"
                  data-testid="nav-link-select-source"
                  // Gated like every other mutating affordance: a protected/busy
                  // state (no create permission, or a save in flight) disables it,
                  // consistent with the viewer's own selection gating (Kimi C5).
                  disabled={!canCreate || busy}
                  title={!canCreate && createReason ? createReason : undefined}
                  style={!canCreate || busy ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn}
                  onClick={() =>
                    onBeginSelect('source', (r) => {
                      setAddPage(String(r.page));
                      setAddRect({ x0: String(r.rect.x0), y0: String(r.rect.y0), x1: String(r.rect.x1), y1: String(r.rect.y1) });
                      setAddSourceText(r.text ?? null);
                      setLocalError(null);
                    })
                  }
                >
                  Select source…
                </button>
              ) : (
                <span style={navStyles.muted} data-testid="nav-link-select-source-unavailable">
                  Live selection is unavailable in this build — enter the rectangle below.
                </span>
              )}
              {rectFromDraft(addRect).ok ? (
                // Keep this user-facing line uncluttered and non-technical: page +
                // "area selected" (+ the selected text when a phrase was picked).
                // The exact coordinates live only in the collapsed advanced detail
                // below (Codex UI-polish note #12).
                <span style={navStyles.muted} data-testid="nav-link-source-summary">
                  page {addPage} · area selected{addSourceText ? ` · “${addSourceText}”` : ''}
                </span>
              ) : (
                <span style={navStyles.muted} data-testid="nav-link-source-empty">
                  no source area chosen yet
                </span>
              )}
            </div>
            {/* Precise numeric entry stays available as an accessible fallback,
                collapsed so the visual flow is the default (prompt item 5). */}
            <details data-testid="nav-link-advanced">
              <summary style={{ ...navStyles.muted, cursor: 'pointer' }}>Enter coordinates manually (advanced)</summary>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
                <label style={navStyles.muted}>
                  Page{' '}
                  <input type="number" min={1} {...(pageCount !== null ? { max: pageCount } : {})} style={{ ...navStyles.input, width: 64 }} aria-label="Hyperlink page" data-testid="nav-link-add-page" value={addPage} onChange={(e) => {
                    // Changing the page by hand drops any rect/text that belonged
                    // to the previously-selected page, so a page N + rect-from-page-M
                    // mismatch can never be staged (Kimi C6 — the same discipline
                    // TargetFields and the RemoteTargetPicker apply to their points).
                    setAddPage(e.target.value);
                    setAddRect(EMPTY_RECT);
                    setAddSourceText(null);
                  }} />
                </label>
                {(['x0', 'y0', 'x1', 'y1'] as const).map((k) => (
                  <label key={k} style={navStyles.muted}>
                    {k}{' '}
                    <input type="number" style={{ ...navStyles.input, width: 64 }} aria-label={`Rectangle ${k}`} data-testid={`nav-link-add-${k}`} value={addRect[k]} onChange={(e) => setAddRect({ ...addRect, [k]: e.target.value })} />
                  </label>
                ))}
                <span style={navStyles.muted}>PDF user-space points; the server re-validates against the artifact.</span>
              </div>
            </details>
            {/* -------- 2. destination -------- */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={navStyles.label}>Destination</span>
              <TargetFields value={addDraft} onChange={setAddDraft} pageCount={pageCount} idPrefix="nav-link-add" documentId={documentId} disabled={!canCreate || busy} {...(onPreview ? { onPreview } : {})} {...(onPreviewFocus ? { onPreviewFocus } : {})} {...(onBeginSelect ? { onBeginSelect } : {})} />
            </div>
            {/* -------- 3. review + commit -------- */}
            <div style={{ ...navStyles.note }} data-testid="nav-link-add-review">
              {rectFromDraft(addRect).ok
                ? `From page ${addPage}${addSourceText ? ` (“${addSourceText}”)` : ''} → To ${describeTargetDraft(addDraft)}`
                : 'Choose a source area (and a destination) to create the link.'}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" style={busy || !canCreate || (addDraft.kind === 'remote' && !addDraft.selection) ? navStyles.secondaryBtnDisabled : navStyles.secondaryBtn} data-testid="nav-link-add-save" disabled={busy || !canCreate || (addDraft.kind === 'remote' && !addDraft.selection)} onClick={submitAdd}>
                Add hyperlink
              </button>
              <button type="button" style={navStyles.secondaryBtn} data-testid="nav-link-add-cancel" onClick={() => { setAdding(false); setAddSourceText(null); }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <RowBtn
            label="Add hyperlink"
            disabled={!canCreate || busy}
            reason={createReason}
            testid="nav-link-add"
            onClick={() => {
              setAdding(true);
              setAddPage('1');
              setAddRect(EMPTY_RECT);
              setAddDraft(EMPTY_LOCAL_TARGET);
              setAddSourceText(null);
              setLocalError(null);
            }}
          />
        )}
      </div>
      {localError && (
        <div style={{ ...navStyles.error, marginTop: 6 }} role="alert" data-testid="nav-link-error">
          {localError}
        </div>
      )}
    </section>
  );
}

interface RowBtnProps {
  readonly label: string;
  readonly disabled: boolean;
  readonly reason: string | null;
  readonly testid: string;
  readonly onClick: () => void;
  /** 'danger' marks a destructive action (Remove); defaults to 'secondary'. */
  readonly tone?: 'secondary' | 'danger';
}

function RowBtn({ label, disabled, reason, testid, onClick, tone = 'secondary' }: RowBtnProps): ReactElement {
  const enabledStyle = tone === 'danger' ? navStyles.dangerBtn : navStyles.secondaryBtn;
  const disabledStyle = tone === 'danger' ? navStyles.dangerBtnDisabled : navStyles.secondaryBtnDisabled;
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={disabled}
      aria-disabled={disabled}
      title={disabled && reason ? reason : undefined}
      style={disabled ? disabledStyle : enabledStyle}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
